import { handleActions, createAction, combineActions } from 'redux-actions';
import {
  createFarmSVR,
  fetchSVRFarm as getSVRFarm,
  getFarmSVR,
  updateFarmSVR,
  createSVRComment,
  updateSVRComment,
  deleteSVRComment,
  detachSVRAsset,
  deleteFarmSVR,
  getSVRChunkAsset,
} from 'endpoints/svr';
import { parseReportResponse, isSVRQuestionBulkPassable } from 'utils/SVRHelper';

// ------------------------------------
// Helpers
// ------------------------------------

const getActionSet = (actionType) => [`${actionType}_PENDING`, `${actionType}_REJECTED`, `${actionType}_FULFILLED`];

// ------------------------------------
// Constants
// ------------------------------------

const initialState = {
  farm: undefined,
  report: undefined,
  unsavedComments: {},
  isLoading: false,
};

// ------------------------------------
// Action Types
// ------------------------------------
const PUSH_UNSAVED_COMMENT = 'siteVisitReport/PUSH_UNSAVED_COMMENT';
const DELETE_UNSAVED_COMMENT = 'siteVisitReport/DELETE_UNSAVED_COMMENT';

const FETCH_SVR_FARM = 'siteVisitReport/FETCH_SVR_FARM';
const [FETCH_SVR_FARM_PENDING, FETCH_SVR_FARM_FULFILLED, FETCH_SVR_FARM_REJECTED] = getActionSet(FETCH_SVR_FARM);

const SAVE_SVR = 'siteVisitReport/SAVE_SVR';
const [SAVE_SVR_PENDING, SAVE_SVR_REJECTED] = getActionSet(SAVE_SVR);

const CREATE_SVR = 'siteVisitReport/CREATE_SVR';
const [CREATE_SVR_PENDING, CREATE_SVR_REJECTED, CREATE_SVR_FULFILLED] = getActionSet(CREATE_SVR);

const DELETE_SVR = 'siteVisitReport/DELETE_SVR';
const [DELETE_SVR_PENDING, DELETE_SVR_REJECTED] = getActionSet(DELETE_SVR);

const PUBLISH_SVR = 'siteVisitReport/PUBLISH_SVR';
const [PUBLISH_SVR_PENDING, PUBLISH_SVR_REJECTED] = getActionSet(PUBLISH_SVR);

const FETCH_SVR = 'siteVisitReport/FETCH_SVR';
const [FETCH_SVR_PENDING, FETCH_SVR_REJECTED, FETCH_SVR_FULFILLED] = getActionSet(FETCH_SVR);

const ANSWER_QUESTION = 'siteVisitReport/ANSWER_QUESTION';
const [ANSWER_QUESTION_PENDING, ANSWER_QUESTION_REJECTED, ANSWER_QUESTION_FULFILLED] = getActionSet(ANSWER_QUESTION);

const ANSWER_CATEGORY_QUESTIONS = 'siteVisitReport/ANSWER_CATEGORY_QUESTIONS';
const [
  ANSWER_CATEGORY_QUESTIONS_PENDING,
  ANSWER_CATEGORY_QUESTIONS_REJECTED,
  ANSWER_CATEGORY_QUESTIONS_FULFILLED,
] = getActionSet(ANSWER_CATEGORY_QUESTIONS);

const UPDATE_GENERAL_NOTES = 'siteVisitReport/UPDATE_GENERAL_NOTES';
const [
  UPDATE_GENERAL_NOTES_PENDING,
  UPDATE_GENERAL_NOTES_REJECTED,
  UPDATE_GENERAL_NOTES_FULFILLED,
] = getActionSet(UPDATE_GENERAL_NOTES);

const UPDATE_ACTION_PLAN = 'siteVisitReport/UPDATE_ACTION_PLAN';
const [
  UPDATE_ACTION_PLAN_PENDING,
  UPDATE_ACTION_PLAN_REJECTED,
  UPDATE_ACTION_PLAN_FULFILLED,
] = getActionSet(UPDATE_ACTION_PLAN);

const ANSWER_ADDITIONAL_QUESTION = 'siteVisitReport/ANSWER_ADDITIONAL_QUESTION';
const [
  ANSWER_ADDITIONAL_QUESTION_PENDING,
  ANSWER_ADDITIONAL_QUESTION_REJECTED,
  ANSWER_ADDITIONAL_QUESTION_FULFILLED,
] = getActionSet(ANSWER_ADDITIONAL_QUESTION);

const POST_COMMENT = 'siteVisitReport/POST_COMMENT';
const [POST_COMMENT_PENDING, POST_COMMENT_REJECTED, POST_COMMENT_FULFILLED] = getActionSet(POST_COMMENT);

const DELETE_COMMENT = 'siteVisitReport/DELETE_COMMENT';
const [DELETE_COMMENT_PENDING, DELETE_COMMENT_REJECTED, DELETE_COMMENT_FULFILLED] = getActionSet(DELETE_COMMENT);

const UPDATE_COMMENT = 'siteVisitReport/UPDATE_COMMENT';
const [UPDATE_COMMENT_PENDING, UPDATE_COMMENT_REJECTED, UPDATE_COMMENT_FULFILLED] = getActionSet(UPDATE_COMMENT);

const UPLOAD_ASSET = 'siteVisitReport/UPLOAD_ASSET';
const [UPLOAD_ASSET_PENDING, UPLOAD_ASSET_REJECTED, UPLOAD_ASSET_FULFILLED] = getActionSet(UPLOAD_ASSET);

const DELETE_ASSET = 'siteVisitReport/DELETE_ASSET';
const [DELETE_ASSET_PENDING, DELETE_ASSET_REJECTED, DELETE_ASSET_FULFILLED] = getActionSet(DELETE_ASSET);

const RESET_DATA = 'siteVisitReport/RESET_DATA';

// ------------------------------------
// Actions
// ------------------------------------

const processAction = async (callback, dispatch, actionType) => {
  try {
    dispatch(createAction(`${actionType}_PENDING`)());
    return callback();
  } catch (error) {
    dispatch(createAction(`${actionType}_REJECTED`)());
    throw error;
  }
};

export const setAnswer = (categoryIndex, questionIndex, answer) => async (dispatch, getState) => {
  return processAction(async () => {
    const { report: { categories, id: svrId }, farm: { id: farmId } } = getState().siteVisitReport;
    const category = categories[categoryIndex];
    const question = category.svr_questions[questionIndex];
    const updatedAnswer = { ...(question.answer || {}), ...answer };
    const updatedQuestion = { ...question, answer: updatedAnswer };
    const requestData = { questions: [updatedQuestion] };
    const response = await updateFarmSVR(farmId, svrId, requestData);
    const data = parseReportResponse(response);
    dispatch(createAction(ANSWER_QUESTION_FULFILLED)(data));
  }, dispatch, ANSWER_QUESTION);
};

export const setCategoryAllPass = (categoryIndex) => async (dispatch, getState) => {
  return processAction(async () => {
    const { report: { categories, id: svrId }, farm: { id: farmId } } = getState().siteVisitReport;
    const category = categories[categoryIndex];
    const questions = category.svr_questions
      .filter(isSVRQuestionBulkPassable)
      .map((question) => ({
        ...question,
        answer: {
          ...(question.answer || {}),
          value: 'pass',
          is_flagged: false,
          is_corrected: false,
          not_corrected: false,
        },
      }));
    const response = await updateFarmSVR(farmId, svrId, { questions });
    const data = parseReportResponse(response);
    dispatch(createAction(ANSWER_CATEGORY_QUESTIONS_FULFILLED)(data));
  }, dispatch, ANSWER_CATEGORY_QUESTIONS);
};

export const updateGeneralNotes = (comment, comment_mentions_attributes) => async (dispatch, getState) => {
  return processAction(async () => {
    const { farm, report: { id: svrId } } = getState().siteVisitReport;
    const response = await updateFarmSVR(
      farm.id,
      svrId,
      { general_notes: comment, questions: [], comment_mentions_attributes }
    );
    const data = parseReportResponse(response);
    dispatch(createAction(UPDATE_GENERAL_NOTES_FULFILLED)(data));
  }, dispatch, UPDATE_GENERAL_NOTES);
};

export const updateActionPlan = (comment, comment_mentions_attributes) => async (dispatch, getState) => {
  return processAction(async () => {
    const { farm, report: { id: svrId } } = getState().siteVisitReport;
    const response = await updateFarmSVR(
      farm.id,
      svrId,
      { action_plan: comment, comment_mentions_attributes, questions: [] }
    );
    const data = parseReportResponse(response);
    dispatch(createAction(UPDATE_ACTION_PLAN_FULFILLED)(data));
  }, dispatch, UPDATE_ACTION_PLAN);
};

export const setAdditionalAnswer = (updatedQuestionIndex, answer) => async (dispatch, getState) => {
  return processAction(async () => {
    const { farm, report: { id: svrId, additionalCategory: { svr_questions } } } = getState().siteVisitReport;
    const questionForUpdate = svr_questions[updatedQuestionIndex];
    questionForUpdate.answer = { ...questionForUpdate.answer, ...answer };
    const response = await updateFarmSVR(farm.id, svrId, { questions: [questionForUpdate] });
    const data = parseReportResponse(response);
    dispatch(createAction(ANSWER_ADDITIONAL_QUESTION_FULFILLED)(data));
  }, dispatch, ANSWER_ADDITIONAL_QUESTION);
};

export const postComment = (categoryIndex, questionIndex, commentData) => async (dispatch, getState) => {
  return processAction(async () => {
    const { categories } = getState().siteVisitReport.report;
    const question = categories[categoryIndex].svr_questions[questionIndex];
    const createdComment = await createSVRComment(question.answer.id, commentData);
    const comments = [createdComment, ...(question.answer.comments || [])];
    const payload = { categoryIndex, questionIndex, answer: { comments } };
    dispatch(createAction(POST_COMMENT_FULFILLED)(payload));
  }, dispatch, POST_COMMENT);
};

export const updateComment = (categoryIndex, questionIndex, commentData) => async (dispatch, getState) => {
  return processAction(async () => {
    const { categories } = getState().siteVisitReport.report;
    const category = categories[categoryIndex];
    const { answer } = category.svr_questions[questionIndex];
    const updatedComment = await updateSVRComment(answer.id, commentData.id, commentData);
    const comments = answer.comments.map((comment) => (comment.id === updatedComment.id ? updatedComment : comment));
    const payload = { categoryIndex, questionIndex, answer: { comments }  };
    dispatch(createAction(UPDATE_COMMENT_FULFILLED)(payload));
  }, dispatch, UPDATE_COMMENT);
};

export const deleteComment = (categoryIndex, questionIndex, commentId) => async (dispatch, getState) => {
  return processAction(async () => {
    const { categories } = getState().siteVisitReport.report;
    const category = categories[categoryIndex];
    const { answer } = category.svr_questions[questionIndex];
    await deleteSVRComment(answer.id, commentId);
    const comments = answer.comments.filter(({ id }) => id !== commentId);
    const payload = { categoryIndex, questionIndex, answer: { comments } };
    dispatch(createAction(DELETE_COMMENT_FULFILLED)(payload));
  }, dispatch, DELETE_COMMENT);
};

export const fetchSVRFarm = (farmId) => async (dispatch) => {
  return processAction(async () => {
    const farm = await getSVRFarm(farmId);
    dispatch(createAction(FETCH_SVR_FARM_FULFILLED)(farm));
  }, dispatch, FETCH_SVR_FARM);
};

export const fetchSVR = (farmId, svrId) => async (dispatch) => {
  return processAction(async () => {
    const response = await getFarmSVR(farmId, svrId);
    const data = parseReportResponse(response);
    dispatch(createAction(FETCH_SVR_FULFILLED)(data));
  }, dispatch, FETCH_SVR);
};

export const publishSVR = () => async (dispatch, getState) => {
  return processAction(async () => {
    const { farm: { id: farmId }, report: { id: svrId } } = getState().siteVisitReport;
    await updateFarmSVR(farmId, svrId, { is_draft: false, questions: [] });
  }, dispatch, PUBLISH_SVR);
};

export const deleteDraftSVR = () => async (dispatch, getState) => {
  return processAction(async () => {
    const { report: { id: svrId }, farm: { id: farmId } } = getState().siteVisitReport;
    return deleteFarmSVR(farmId, svrId);
  }, dispatch, DELETE_SVR);
};

export const createDraftSVR = (farmId) => async (dispatch) => {
  return processAction(async () => {
    const createdSVR = await createFarmSVR(farmId, { is_draft: true, questions: [] });
    const data = parseReportResponse(createdSVR);
    dispatch(createAction(CREATE_SVR_FULFILLED)(data));
    return createdSVR.id;
  }, dispatch, CREATE_SVR);
};

export const uploadAsset = (asset) => async (dispatch, getState) => {
  return processAction(async () => {
    const { report: { id, svr_assets } } = getState().siteVisitReport;
    const attachedAsset = await getSVRChunkAsset(id, { chunk_identifier: asset.uniqueIdentifier });
    const updatedAssets = [...(svr_assets || []), attachedAsset];
    dispatch(createAction(UPLOAD_ASSET_FULFILLED)(updatedAssets));
  }, dispatch, UPLOAD_ASSET);
};

export const deleteAsset = (assetForDelete) => async (dispatch, getState) => {
  return processAction(async () => {
    const { report: { svr_assets, id } } = getState().siteVisitReport;
    await detachSVRAsset(id, assetForDelete);
    const updatedAssets = svr_assets.filter(({ id }) => id !== assetForDelete.id) || [];
    dispatch(createAction(UPLOAD_ASSET_FULFILLED)(updatedAssets));
  }, dispatch, DELETE_ASSET);
};

export const pushUnsavedComment = (comment) => async (dispatch) => {
  dispatch(createAction(PUSH_UNSAVED_COMMENT)(comment));
};

export const deleteUnsavedComment = (commentIdx) => async (dispatch) => {
  dispatch(createAction(DELETE_UNSAVED_COMMENT)(commentIdx));
};

export const resetData = createAction(RESET_DATA);

// ------------------------------------
// Reducer
// ------------------------------------
export default handleActions({
  // PENDING ACTIONS
  [combineActions(
    FETCH_SVR_FARM_PENDING,
    FETCH_SVR_PENDING,
    SAVE_SVR_PENDING,
    CREATE_SVR_PENDING,
    DELETE_SVR_PENDING,
    PUBLISH_SVR_PENDING,
    ANSWER_QUESTION_PENDING,
    ANSWER_CATEGORY_QUESTIONS_PENDING,
    UPDATE_COMMENT_PENDING,
    UPLOAD_ASSET_PENDING,
    DELETE_ASSET_PENDING,
    DELETE_COMMENT_PENDING,
    ANSWER_ADDITIONAL_QUESTION_PENDING,
    UPDATE_GENERAL_NOTES_PENDING,
    UPDATE_ACTION_PLAN_PENDING,
    POST_COMMENT_PENDING,
    PUBLISH_SVR_PENDING,
  )]: (state) => ({
    ...state,
    isLoading: true,
  }),
  // REJECTED ACTIONS
  [combineActions(
    FETCH_SVR_FARM_REJECTED,
    FETCH_SVR_REJECTED,
    SAVE_SVR_REJECTED,
    CREATE_SVR_REJECTED,
    DELETE_SVR_REJECTED,
    PUBLISH_SVR_REJECTED,
    ANSWER_QUESTION_REJECTED,
    ANSWER_CATEGORY_QUESTIONS_REJECTED,
    UPDATE_COMMENT_REJECTED,
    UPLOAD_ASSET_REJECTED,
    DELETE_ASSET_REJECTED,
    DELETE_COMMENT_REJECTED,
    UPDATE_GENERAL_NOTES_REJECTED,
    UPDATE_ACTION_PLAN_REJECTED,
    ANSWER_ADDITIONAL_QUESTION_REJECTED,
    POST_COMMENT_REJECTED,
    PUBLISH_SVR_REJECTED,
  )]: (state, action) => ({
    ...state,
    ...action.payload,
    isLoading: false,
  }),
  // FULFILLED ACTIONS
  [combineActions(
    FETCH_SVR_FULFILLED,
    CREATE_SVR_FULFILLED,
    ANSWER_QUESTION_FULFILLED,
    ANSWER_CATEGORY_QUESTIONS_FULFILLED,
    ANSWER_ADDITIONAL_QUESTION_FULFILLED,
    UPDATE_GENERAL_NOTES_FULFILLED,
    UPDATE_ACTION_PLAN_FULFILLED,
  )]: (state, action) => ({
    ...state,
    report: action.payload,
    isLoading: false,
  }),
  // COMMENTS
  [combineActions(
    POST_COMMENT_FULFILLED,
    DELETE_COMMENT_FULFILLED,
    UPDATE_COMMENT_FULFILLED,
  )]: (state, { payload: { categoryIndex, questionIndex, answer } }) => {
    const { report } = state;
    const { categories } = report;
    const category = report.categories[categoryIndex];
    const { svr_questions: categoryQuestions } = category;
    const updatedQuestion = category.svr_questions[questionIndex];

    return {
      ...state,
      report: {
        ...report,
        categories: [
          ...categories.slice(0, categoryIndex),
          {
            ...category,
            svr_questions: [
              ...categoryQuestions.slice(0, questionIndex),
              {
                ...updatedQuestion,
                answer: {
                  ...(updatedQuestion.answer || {}),
                  ...answer
                }
              },
              ...categoryQuestions.slice(questionIndex + 1),
            ],
          },
          ...categories.slice(categoryIndex + 1),
        ],
      },
      isLoading: false,
    };
  },
  // ASSETS
  [combineActions(
    DELETE_ASSET_FULFILLED,
    UPLOAD_ASSET_FULFILLED,
  )]: (state, { payload }) => {
    const { report } = state;

    return {
      ...state,
      report: {
        ...report,
        svr_assets: payload,
      },
      isLoading: false,
    };
  },
  [FETCH_SVR_FARM_FULFILLED]: (state, action) => ({
    ...state,
    farm: action.payload,
    isLoading: false,
  }),
  // UNSAVED_COMMENTS
  [PUSH_UNSAVED_COMMENT]: (state, action) => ({
    ...state,
    unsavedComments: {
      ...state.unsavedComments,
      [action.payload.key]: { ...(state.unsavedComments?.[action.payload.key] || {}), ...action.payload },
    }
  }),
  [DELETE_UNSAVED_COMMENT]: (state, action) => {
    const unsavedCommentsCopy = { ...state.unsavedComments };
    delete unsavedCommentsCopy[action.payload];
    return { ...state, unsavedComments: unsavedCommentsCopy };
  },
  // RESETING
  [RESET_DATA]: () => ({
    ...initialState,
  }),
  'global/RESET_STORE': () => ({
    ...initialState,
  }),
}, initialState);
